import { ethers } from "ethers";
import { useQuery } from "react-query";
import React, { useCallback, useEffect } from "react";
import { abi } from "./ABI";

const usePool = (props) => {
  const poolInfo = useQuery(
    ["depositEnabled", props.poolAddress],
    async () => {
      const contract = new ethers.Contract(
        props.poolAddress,
        abi,
        props.provider
      );
      const pool = await contract.poolInfo();

      return {
        depositEnabled: pool.depositEnabled,
        poolAddress: props.poolAddress,
        apy: pool.apy,

        totalStaked: {
          value: pool.totalLocked,
          formatted: ethers.utils.formatUnits(pool.totalLocked, props.decimals),
          decimals: props.decimals,
        },
        rewardsLeft: {
          value: pool.rewardsLeft,
          formatted: ethers.utils.formatUnits(pool.rewardsLeft, props.decimals),
          decimals: props.decimals,
        },
        claimPeriod: pool.claimPeriodInDays,
        lockPeriod: pool.lockPeriodInDays,
      };
    },
    {
      enabled: !!props.poolAddress && !!props.provider,
    }
  );

  const userDeposits = useQuery(
    ["userDeposits", props.poolAddress, props.account],
    async () => {
      const contract = new ethers.Contract(
        props.poolAddress,
        abi,
        props.provider
      );

      const deposits = await contract.getAllUserDeposits(props.account);

      return deposits.map((deposit) => {
        return {
          amount: {
            value: deposit.amount,
            formatted: ethers.utils.formatUnits(deposit.amount, props.decimals),
            decimals: props.decimals,
          },
          depositTime: {
            value: Number(deposit.depositTime),
            date: new Date(Number(deposit.depositTime) * 1000),
          },
          claims: Number(deposit.claims),
          claimableRewards: {
            value: deposit.claimableRewards,
            formatted: ethers.utils.formatUnits(
              deposit.claimableRewards,
              props.decimals
            ),
            decimals: props.decimals,
          },
          generatedRewards: {
            value: deposit.generatedRewards,
            formatted: ethers.utils.formatUnits(
              deposit.generatedRewards,
              props.decimals
            ),
            decimals: props.decimals,
          },
        };
      });
    },
    {
      enabled: !!props.poolAddress && !!props.provider && !!props.account,
    }
  );

  const [userLocked, setUserLocked] = React.useState(null);
  const [userUnlocked, setUserUnlocked] = React.useState(null);
  const [firstUnlock, setFirstUnlock] = React.useState(null);
  const [userGeneratedRewards, setUserGeneratedRewards] = React.useState(null);
  const [userClaimableRewards, setUserClaimableRewards] = React.useState(null);

  useEffect(() => {
    if (userDeposits.data) {
      const zero = {
        value: ethers.BigNumber.from(0),
        formatted: "0",
        decimals: props.decimals,
      };
      if (userDeposits.data.length === 0) {
        setUserLocked(zero);
        setUserUnlocked(zero);
        setFirstUnlock(false);
        setUserGeneratedRewards(zero);
        setUserClaimableRewards(zero);
      } else {
        let generated = ethers.BigNumber.from(0);
        let claimable = ethers.BigNumber.from(0);
        let locked = ethers.BigNumber.from(0);
        let unlocked = ethers.BigNumber.from(0);
        let fUnlock = 2 ** 256;

        userDeposits.data.forEach((deposit) => {
          if (
            deposit.depositTime.value + poolInfo.data?.lockPeriod * 86400 >
            Date.now() / 1000
          ) {
            locked = deposit.amount.value.add(locked);
            if (
              deposit.depositTime.value + poolInfo.data.lockPeriod * 86400 <
              fUnlock
            )
              fUnlock =
                deposit.depositTime.value + poolInfo.data.lockPeriod * 86400;
          } else {
            unlocked = unlocked.add(deposit.amount.value);
          }
          generated = generated.add(deposit.generatedRewards.value);
          claimable = claimable.add(deposit.claimableRewards.value);
        });

        setUserUnlocked({
          value: unlocked,
          formatted: ethers.utils.formatUnits(unlocked, props.decimals),
          decimals: props.decimals,
        });
        setUserLocked({
          value: locked,
          formatted: ethers.utils.formatUnits(locked, props.decimals),
          decimals: props.decimals,
        });

        setFirstUnlock({
          value: fUnlock,
          date: new Date(fUnlock * 1000),
        });

        setUserGeneratedRewards({
          value: generated,
          formatted: ethers.utils.formatUnits(generated, props.decimals),
          decimals: props.decimals,
        });
        setUserClaimableRewards({
          value: claimable,
          formatted: ethers.utils.formatUnits(claimable, props.decimals),
          decimals: props.decimals,
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.decimals, userDeposits.data?.length, poolInfo.data?.lockPeriod]);

  const deposit = useCallback(
    async (amount, signer) => {
      const contract = new ethers.Contract(props.poolAddress, abi, signer);
      const tr = await contract.deposit(amount);
      await tr.wait();

      await poolInfo.refetch();
      await userDeposits.refetch();

      return tr;
    },
    [props.poolAddress, poolInfo, userDeposits]
  );

  const claim = useCallback(
    async (signer) => {
      const contract = new ethers.Contract(props.poolAddress, abi, signer);
      const tr = await contract.claimAll();
      await tr.wait();

      await poolInfo.refetch();
      await userDeposits.refetch();

      return tr;
    },
    [props.poolAddress, poolInfo, userDeposits]
  );

  const withdraw = useCallback(
    async (signer) => {
      const contract = new ethers.Contract(props.poolAddress, abi, signer);
      const tr = await contract.withdrawAllUnlocked();
      await tr.wait();

      await poolInfo.refetch();
      await userDeposits.refetch();

      return tr;
    },
    [props.poolAddress, poolInfo, userDeposits]
  );

  return {
    poolInfo,
    userDeposits,
    deposit,
    claim,
    withdraw,
    userLocked,
    userUnlocked,
    userClaimableRewards,
    userGeneratedRewards,
    firstUnlock,
  };
};

export default usePool;
